// src/context/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

interface User {
  userName: string;
  userRole: number; // 0 for user, 1 for admin
}

interface AuthContextType {
  user: User | null;
  login: (userName: string, userRole: number) => void;
  logout: () => void;
  isLoading: boolean; // To indicate if initial load check is complete
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUserState] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  const [ setUser] = useState<User | null>(null);
const isLoggedIn = !!user;

  useEffect(() => {
    const storedUserName = localStorage.getItem('userName');
    const storedUserRole = localStorage.getItem('userRole');

    if (storedUserName && storedUserRole !== null) {
      setUserState({
        userName: storedUserName,
        userRole: parseInt(storedUserRole, 10),
      });
    }
    setIsLoading(false);
  }, []);

  const login = (userName: string, userRole: number) => {
    setUserState({ userName, userRole });
    localStorage.setItem('userName', userName);
    localStorage.setItem('userRole', userRole.toString());
  };

  const logout = () => {
    setUserState(null);
    localStorage.removeItem('userName');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userToken');
    navigate('/');
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};